import { useEffect, useRef, useState } from "react";
import { Redirect, useLocation } from "wouter";
import { AuthLayout } from "../components/AuthLayout";
import { setMerchantToken } from "../lib/merchantAuth";
import {
  clearSignupAttempt,
  getSignupAttempt,
} from "./MerchantSignup";

/**
 * /merchant/signup/complete — Phase 28 step 3 of 3 (mint tenant).
 *
 * Auto-fires the create-tenant call on mount. The server returns the
 * plaintext API token exactly once — we display it for the merchant
 * to copy AND auto-login by stashing it via setMerchantToken(). The
 * "Continue to dashboard" button then navigates to /merchant/dashboard
 * which (already wired since Phase 13) reads that token and works.
 *
 * D94: auto-login is just localStorage; no tenant-auth bridge needed.
 */
interface CompleteResponse {
  tenantId: string;
  name: string;
  joinShortCode: string;
  voucherPrefix: string;
  apiTokenPrefix: string;
  plainTextApiToken: string;
  autoLogin: { token: string; dashboardUrl: string };
}

export function MerchantSignupCompletePage() {
  const [, navigate] = useLocation();
  const stash = getSignupAttempt();
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<CompleteResponse | null>(null);
  const [copied, setCopied] = useState(false);
  // StrictMode in dev double-invokes effects; guard against firing
  // the create-tenant POST twice with a ref so the merchant doesn't
  // accidentally see ALREADY_COMPLETED on their first render.
  const fired = useRef(false);

  useEffect(() => {
    if (!stash) return;
    if (fired.current) return;
    fired.current = true;

    (async () => {
      try {
        const res = await fetch("/api/v1/public/merchant-signup/complete", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ attemptToken: stash.attemptToken }),
        });
        const json = (await res.json().catch(() => null)) as
          | { data?: CompleteResponse; error?: { code?: string; message?: string } }
          | null;
        if (!res.ok || !json?.data) {
          setErr(json?.error?.message ?? "Could not finish creating your account.");
          return;
        }
        // Stash the token immediately so even if the merchant closes
        // the tab without clicking "Continue", they're auto-logged-in
        // when they next open /merchant/dashboard. The plaintext is
        // ALSO shown on screen so they can copy + paste it into a
        // password manager — this is the only time we'll show it.
        setMerchantToken(json.data.autoLogin.token);
        setResult(json.data);
        clearSignupAttempt();
      } catch {
        setErr("Network error while creating your account. Please try again.");
      } finally {
        setBusy(false);
      }
    })();
  }, [stash]);

  if (!stash && !result) {
    return <Redirect to="/merchant/signup" />;
  }

  async function onCopyToken() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.plainTextApiToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard not available — merchant can still select+copy */
    }
  }

  if (busy) {
    return (
      <AuthLayout
        title="Creating your account…"
        subtitle="One moment — minting your API token now."
      >
        <p
          className="text-center text-sm text-ink-dark"
          data-testid="text-signup-complete-busy"
        >
          Please don't close this page.
        </p>
      </AuthLayout>
    );
  }

  if (err) {
    return (
      <AuthLayout title="Something went wrong">
        <p
          className="rounded-card bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="text-signup-complete-error"
        >
          {err}
        </p>
        <button
          type="button"
          onClick={() => navigate("/merchant/signup")}
          className="btn-primary w-full mt-4"
          data-testid="button-signup-complete-restart"
        >
          Start over
        </button>
      </AuthLayout>
    );
  }

  if (!result) return null;

  return (
    <AuthLayout
      title={`Welcome, ${result.name}!`}
      subtitle="Save your API token before you continue — we'll only show it once."
    >
      <div className="space-y-5">
        <div className="rounded-card bg-blue-50 px-4 py-3 text-sm text-ink-dark">
          <p className="font-semibold text-navy">Your API token</p>
          <p
            className="mt-2 break-all rounded bg-white px-2 py-2 font-mono text-xs"
            data-testid="text-signup-complete-token"
          >
            {result.plainTextApiToken}
          </p>
          <button
            type="button"
            onClick={onCopyToken}
            className="btn-secondary w-full mt-3"
            data-testid="button-signup-complete-copy-token"
          >
            {copied ? "Copied!" : "Copy token"}
          </button>
          <p className="mt-2 text-xs text-amber-700">
            Store this somewhere safe. You can recover it later via
            "Recover token" using your owner email.
          </p>
        </div>

        <dl className="rounded-card bg-cream-50 px-4 py-3 text-sm space-y-2">
          <div className="flex justify-between">
            <dt className="text-ink-dark">Join short code</dt>
            <dd
              className="font-mono text-navy"
              data-testid="text-signup-complete-short-code"
            >
              {result.joinShortCode}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-dark">Voucher prefix</dt>
            <dd
              className="font-mono text-navy"
              data-testid="text-signup-complete-voucher-prefix"
            >
              {result.voucherPrefix}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-dark">Token prefix</dt>
            <dd className="font-mono text-navy">{result.apiTokenPrefix}…</dd>
          </div>
        </dl>

        <button
          type="button"
          onClick={() => navigate(result.autoLogin.dashboardUrl)}
          className="btn-primary w-full"
          data-testid="button-signup-continue-to-dashboard"
        >
          Continue to dashboard
        </button>
      </div>
    </AuthLayout>
  );
}
